import React from 'react';
import { motion } from 'framer-motion';
import Button from '../ui/Button';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Terms and Conditions
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh] custom-scrollbar space-y-8">

          {/* Acceptance of Terms */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Acceptance of Terms</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              By accessing and using the <strong>Scentral</strong> application (the "Service"), you
              accept and agree to be bound by these Terms and Conditions. If you do not agree, you
              may not use the Service.
            </p>
          </div>

          {/* Description of Service */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Description of Service
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <p className="text-gray-900 dark:text-white text-sm mb-1">
                Scentral allows users to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li>Track and organize their personal fragrance collections</li>
                <li>Share their collections with user controlled privacy settings</li>
                <li>Rate fragrances (seasonality, longevity, sillage, etc.)</li>
                <li>Store personal notes and preferences</li>
                <li>Import and export collection data</li>
                <li>View collection statistics and insights</li>
              </ul>
            </div>
          </div>

          {/* User Accounts */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              User Accounts
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li>You must provide accurate and complete information when creating an account.</li>
                <li>You are responsible for maintaining the confidentiality of your credentials.</li>
                <li>You must be at least 13 years old (or older if required by local law).</li>
                <li>Only one account per person is permitted.</li>
              </ul>
            </div>
          </div>

          {/* User Generated Content */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              User Generated Content
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li>Users must not submit illegal, offensive, or infringing content.</li>
                <li>You are solely responsible for any content you submit, and you assume all liability for your submissions.</li>
                <li>Scentral reserves the right to remove or restrict access to any content that violates these Terms, applicable laws, or our policies.</li>
              </ul>
            </div>
          </div>

          {/* Your Collection Data */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Your Collection Data
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li>You retain ownership of all fragrance data you input into the Service.</li>
                <li>Your data is private and not shared without your explicit consent.</li>
                <li>You are responsible for the accuracy of the information you provide.</li>
                <li>You can export your data at any time using the CSV export feature.</li>
              </ul>
            </div>
          </div>

          {/* Privacy and Security */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Privacy and Security
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li>We implement industry-standard security measures to protect your data.</li>
                <li>We do not sell or share your personal information with third parties for marketing.</li>
                <li>Your collection is private by default.</li>
                <li>See our Privacy Policy for more details on data handling.</li>
              </ul>
            </div>
          </div>

          {/* Account Termination */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Account Termination
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li>You may delete your account at any time in your account settings.</li>
                <li>Your data will be removed from active systems upon termination.</li>
                <li>Residual backups are purged periodically in the normal course of business.</li>
                <li>Scentral reserves the right to terminate your account if you violate these Terms.</li>
              </ul>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Limitation of Liability
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li>The Service is provided “as-is” and “as available.”</li>
                <li>Scentral reserves the right to modify or discontinue the Service at any time without notice.</li>
                <li>Scentral is not liable for any data loss, service interruptions, or inaccuracies in user-submitted content.</li>
                <li>No warranties, express or implied, are provided regarding the accuracy, reliability, or availability of the Service.</li>
              </ul>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-3 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              For questions about these Terms and Conditions, please contact us at{' '}
              <a
                href="mailto:support@scentral.app"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                support@scentral.app
              </a>.
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-center">
            <Button onClick={onClose}>I Agree</Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TermsModal;